import React from "react";
import { useParams, Navigate } from "react-router-dom";
import { Link } from "../components/L";
import { ArrowLeft, ArrowRight, Clock } from "lucide-react";
import Seo from "../components/Seo";
import CTABand from "../components/CTABand";
import { getArticle, articles } from "../blog";
import { parseBody } from "../pageContent";
import { useLang } from "../context/LanguageContext";

const Block = ({ block }) => {
  if (block.h2) return <h2 className="font-head font-semibold text-[#12211b] text-2xl md:text-3xl mt-10 mb-4">{block.h2}</h2>;
  if (block.p) return <p className="text-slate-700 text-lg leading-relaxed mb-4">{block.p}</p>;
  if (block.ul) return (
    <ul className="space-y-2.5 mb-4">
      {block.ul.map((li, i) => (
        <li key={i} className="flex items-start gap-3 text-slate-700 text-[17px]"><span className="w-2 h-2 rounded-full bg-[#d9b45a] mt-2.5 shrink-0" />{li}</li>
      ))}
    </ul>
  );
  return null;
};

const BlogArticle = () => {
  const { slug } = useParams();
  const { t } = useLang();
  const b = t.blog;
  const article = getArticle(slug);
  if (!article) return <Navigate to="/ratgeber" replace />;

  const pc = (t._pages || {})[`article:${slug}`] || {};
  const title = pc.title || article.title;
  const excerpt = pc.excerpt || article.excerpt;
  const blocks = pc.body ? parseBody(pc.body) : article.content;

  const related = articles.filter((a) => a.slug !== slug && a.cat === article.cat).slice(0, 3);
  const fallback = articles.filter((a) => a.slug !== slug).slice(0, 3);
  const show = related.length ? related : fallback;

  const jsonLd = {
    "@context": "https://schema.org", "@type": "Article",
    headline: title, description: excerpt, author: { "@type": "Organization", name: "MyCleaner.ch" },
  };

  return (
    <div>
      <Seo pageKey={`article:${slug}`} title={`${title} | MyCleaner.ch Ratgeber`} description={excerpt} keywords={`${title}, Putzfrau Vermittlung Schweiz, Putzfrau gesucht, Haushaltshilfe`} jsonLd={jsonLd} />

      <article className="pt-[116px]">
        <div className="max-w-[760px] mx-auto px-5 md:px-6">
          <Link to="/ratgeber" className="inline-flex items-center gap-2 text-teal-700 font-semibold text-sm mb-6 hover:gap-3 transition-all"><ArrowLeft className="w-4 h-4" /> {b.back}</Link>
          <div className="flex items-center gap-3 mb-4 text-sm">
            <span className="bg-teal-50 text-teal-700 font-semibold px-3 py-1 rounded-full">{article.cat}</span>
            <span className="flex items-center gap-1 text-slate-400"><Clock className="w-4 h-4" /> {article.min} {b.minRead}</span>
          </div>
          <h1 className="font-head font-semibold text-[#12211b] text-4xl md:text-[46px] leading-[1.1] mb-8">{title}</h1>
          <div className="prose-rich">
            {blocks.map((block, i) => (<Block key={i} block={block} />))}
          </div>

          <div className="mt-12 bg-[#faf6ee] border border-[#ece4d3] rounded-2xl p-8 text-center">
            <h3 className="font-head font-semibold text-2xl text-[#12211b] mb-3">{t.cta.titleDefault}</h3>
            <p className="text-slate-600 mb-6">{t.cta.desc}</p>
            <Link to="/buchen" className="inline-flex items-center gap-2 bg-teal-700 text-white font-head font-semibold text-lg px-8 py-4 rounded-xl hover:bg-teal-800 transition-colors">{b.cta} <ArrowRight className="w-5 h-5" /></Link>
          </div>
        </div>

        <div className="max-w-[1200px] mx-auto px-5 md:px-6 py-16">
          <h2 className="font-head font-semibold text-2xl text-[#12211b] mb-6">{b.related}</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {show.map((a) => (
              <Link key={a.slug} to={`/ratgeber/${a.slug}`} className="group bg-white rounded-2xl border border-[#ece4d3] p-6 hover:shadow-lg hover:border-[#d9b45a]/60 transition-all">
                <span className="text-xs bg-teal-50 text-teal-700 font-semibold px-2.5 py-1 rounded-full">{a.cat}</span>
                <h3 className="font-head font-semibold text-[#12211b] text-lg leading-snug mt-3 mb-2 group-hover:text-teal-700 transition-colors">{a.title}</h3>
                <span className="inline-flex items-center gap-1.5 text-teal-700 font-semibold text-sm">{b.readMore} <ArrowRight className="w-4 h-4" /></span>
              </Link>
            ))}
          </div>
        </div>
      </article>

      <CTABand />
    </div>
  );
};

export default BlogArticle;
